import React, { useState, useEffect } from 'react';
import api from '../../api/client';

// clipboard API needs HTTPS — fall back to execCommand on plain HTTP
const copyText = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text);
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return Promise.resolve();
};

const iconBtn = {
    border: 'none', background: 'none', color: '#64748b', cursor: 'pointer',
    padding: '2px 4px', fontSize: 13,
};

// Varieties included in a mix-type seed (free text, one per line), keyed by seedsID.
// Shown under the seed selector in WildmicrPopup; hidden entirely for non-mix seeds
// except a small "add" link.
const SeedMixListField = ({ seedsID }) => {
    const [text, setText] = useState('');
    const [draft, setDraft] = useState('');
    const [editing, setEditing] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setEditing(false); setCopied(false); setError('');
        if (!seedsID) { setText(''); return; }
        api.get(`/api/seed-mix-list/${seedsID}`)
            .then(r => setText(r.data.mix_list || ''))
            .catch(() => setText(''));
    }, [seedsID]);

    if (!seedsID) return null;

    const lines = text.split('\n').map(s => s.trim()).filter(Boolean);

    const save = async () => {
        try {
            await api.put(`/api/seed-mix-list/${seedsID}`, { mix_list: draft });
            setText(draft);
            setEditing(false);
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        }
    };

    const copy = () => copyText(lines.join('\n')).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    });

    if (!lines.length && !editing) {
        return (
            <div style={{ marginTop: 8 }}>
                <button type="button" onClick={() => { setDraft(''); setEditing(true); }}
                    style={{ border: 'none', background: 'none', color: '#437045', fontSize: 12, cursor: 'pointer', padding: 0 }}>
                    <i className="fas fa-plus" style={{ marginRight: 4 }}></i>Add mix composition
                </button>
            </div>
        );
    }

    return (
        <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span className="kit-field__label" style={{ margin: 0 }}
                    title="Varieties included in this seed mix">
                    Mix Composition{lines.length > 0 ? ` (${lines.length})` : ''}
                </span>
                {!editing && (
                    <>
                        <button type="button" onClick={copy} title="Copy list" style={iconBtn}>
                            <i className={copied ? 'fas fa-check' : 'far fa-copy'}
                                style={copied ? { color: '#28a745' } : undefined}></i>
                        </button>
                        <button type="button" onClick={() => { setDraft(text); setEditing(true); }}
                            title="Edit list" style={iconBtn}>
                            <i className="fas fa-pen"></i>
                        </button>
                    </>
                )}
            </div>

            {editing ? (
                <>
                    <textarea
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        rows={Math.min(16, Math.max(6, draft.split('\n').length + 1))}
                        placeholder={'One variety per line…'}
                        style={{
                            width: '100%', boxSizing: 'border-box', padding: 8,
                            border: '1px solid #dde1e7', borderRadius: 6,
                            fontSize: 13, resize: 'vertical',
                        }}
                    />
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <button type="button" onClick={save}
                            style={{
                                padding: '5px 14px', background: '#28a745', color: '#fff',
                                border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            }}>
                            Save
                        </button>
                        <button type="button" onClick={() => setEditing(false)}
                            style={{
                                padding: '5px 14px', background: '#fff', color: '#475569',
                                border: '1px solid #dde1e7', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                            }}>
                            Cancel
                        </button>
                    </div>
                </>
            ) : (
                <div style={{
                    background: '#f8fafc', border: '1px solid #eef1f4', borderRadius: 8,
                    padding: '8px 12px', fontSize: 13, color: '#334155',
                    WebkitColumnWidth: 180, columnWidth: 180,
                    WebkitColumnGap: 24, columnGap: 24,
                }}>
                    {lines.map((l, i) => (
                        <div key={i} style={{ WebkitColumnBreakInside: 'avoid', breakInside: 'avoid', padding: '2px 0' }}>
                            {l}
                        </div>
                    ))}
                </div>
            )}
            {error && <div style={{ color: '#c0392b', fontSize: 12, marginTop: 4 }}>⚠ {error}</div>}
        </div>
    );
};

export default SeedMixListField;
